import {
  areaName,
  distanceM,
  pathLengthM,
  ROUTE_CATEGORY_LABELS,
  ROUTE_MODE_LABELS,
  ROUTE_STATUS_LABELS,
  ROUTE_STATUSES,
  type AreaSummary,
  type AssetDetail,
  type LngLat,
  type PolePreview,
  type RouteDetail,
  type RouteMode,
} from '@ftthnms/shared';
import { Button, regionColor } from '@ftthnms/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Layers,
  MapPin,
  Maximize,
  Minus,
  Move,
  PenLine,
  Plus,
  Route as RouteIcon,
  Undo2,
} from 'lucide-react';
import type { FeatureCollection as GjFeatureCollection, Geometry, Point } from 'geojson';
import maplibregl, { type AddLayerObject, type GeoJSONSource, type Map as MlMap } from 'maplibre-gl';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { TerraDraw, TerraDrawLineStringMode, TerraDrawPolygonMode, TerraDrawSelectMode } from 'terra-draw';
import { TerraDrawMapLibreGLAdapter } from 'terra-draw-maplibre-gl-adapter';
import { AssetDrawer } from '../components/AssetDrawer';
import { AssetForm } from '../components/AssetForm';
import { RouteDrawer } from '../components/RouteDrawer';
import { RouteForm } from '../components/RouteForm';
import { useToast } from '../components/Toast';
import { api, metres, nf, qs, send } from '../lib/api';
import { invalidateAssets } from '../lib/assets';
import { colorName } from '../lib/colors';
import { parseLatLng } from '../lib/coords';
import type { RouteDraft } from '../lib/routes';
import { useSession } from '../lib/session';
import { useTheme } from '../lib/theme';
import { AssetGlyph } from '../map/AssetGlyph';
import { buildStyle, LABEL_FONT, MARKER_FONT, token, type Basemap } from '../map/style';

interface FeatureCollection {
  type: 'FeatureCollection';
  truncated: boolean;
  features: Array<{
    type: 'Feature';
    id: string;
    geometry: { type: 'Point'; coordinates: [number, number] };
    properties: {
      id: string;
      code: string;
      name: string;
      type: string;
      status: string;
      area: string | null;
      sample: boolean;
      mon: string | null;
    };
  }>;
}

interface RouteCollection {
  type: 'FeatureCollection';
  truncated: boolean;
  features: Array<{
    type: 'Feature';
    id: string;
    geometry: { type: 'LineString'; coordinates: LngLat[] };
    properties: {
      id: string;
      name: string;
      status: string;
      category: string;
      mode: string;
      color: string | null;
      lengthM: number;
    };
  }>;
}

/** An asset picked on the map as a route end. */
interface End {
  id: string;
  code: string;
  typeCode: string;
  lng: number;
  lat: number;
}

type Panel =
  | { kind: 'view'; id: string }
  | { kind: 'edit'; asset: AssetDetail }
  | { kind: 'create'; lat: number; lng: number }
  | { kind: 'route'; id: string }
  | { kind: 'route-edit'; route: RouteDetail }
  | { kind: 'route-create'; draft: RouteDraft };

type Mode =
  | null
  | { kind: 'add' }
  | { kind: 'move'; asset: AssetDetail; to?: { lat: number; lng: number } }
  | { kind: 'route'; routeMode: RouteMode; from?: End; vertices: LngLat[] }
  | { kind: 'shape'; route: RouteDetail };

const TYPE_COLOR_TOKEN: Record<string, string> = {
  OLT: 'asset-olt',
  SERVER: 'asset-server',
  AP: 'asset-ap',
  ODC: 'asset-odc',
  ODP: 'asset-odp',
  JC: 'asset-jc',
  POLE: 'asset-pole',
  CPE: 'asset-cpe',
};

const LAYER_GROUPS: Array<{ label: string; types: string[]; glyph: string }> = [
  { label: 'OLT & Server', types: ['OLT', 'SERVER', 'AP'], glyph: 'OLT' },
  { label: 'ODC', types: ['ODC'], glyph: 'ODC' },
  { label: 'ODP', types: ['ODP'], glyph: 'ODP' },
  { label: 'Joint Closure', types: ['JC'], glyph: 'JC' },
  { label: 'Tiang Tumpu', types: ['POLE'], glyph: 'POLE' },
  { label: 'Pelanggan', types: ['CPE'], glyph: 'CPE' },
];

const INDONESIA_VIEW = { center: [107.2, -6.3] as [number, number], zoom: 7 };
const EMPTY: GjFeatureCollection = { type: 'FeatureCollection', features: [] };
const DRAWER_PADDING = { right: 420, top: 0, bottom: 0, left: 0 };

export function MapPage({ focus, routeId }: { focus?: string; routeId?: string }) {
  const { area, ability } = useSession();
  const { theme } = useTheme();
  const queryClient = useQueryClient();
  const toast = useToast();
  const container = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MlMap | null>(null);
  const drawRef = useRef<TerraDraw | null>(null);
  const fittedRef = useRef(false);

  const [basemap, setBasemap] = useState<Basemap>('theme');
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const [hiddenRoutes, setHiddenRoutes] = useState<Set<string>>(new Set());
  // Route line colours switched off in the legend ('' = routes without their own colour).
  const [hiddenColors, setHiddenColors] = useState<Set<string>>(new Set());
  const [showMon, setShowMon] = useState(true);
  const [panel, setPanel] = useState<Panel | null>(
    routeId ? { kind: 'route', id: routeId } : focus ? { kind: 'view', id: focus } : null,
  );
  const [flyId, setFlyId] = useState<string | null>(focus ?? null);
  const [fitRouteId, setFitRouteId] = useState<string | null>(routeId ?? null);
  const [mode, setMode] = useState<Mode>(null);
  const [traceCoords, setTraceCoords] = useState<LngLat[] | null>(null);
  const [polePreview, setPolePreview] = useState<PolePreview['poles'] | null>(null);
  const [styleVersion, setStyleVersion] = useState(0);
  const [drawArea, setDrawArea] = useState<string | null>(null);
  const [drawnGeom, setDrawnGeom] = useState<unknown | null>(null);
  const [saving, setSaving] = useState(false);
  const [routing, setRouting] = useState(false);
  const [coordText, setCoordText] = useState('');
  const [coordError, setCoordError] = useState<string | null>(null);

  // The map's handlers are registered once; they read the latest state through refs.
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const panelRef = useRef(panel);
  panelRef.current = panel;

  const features = useQuery({
    queryKey: ['map-features', area],
    queryFn: () => api<FeatureCollection>(`/api/v1/map/features${qs({ area })}`),
  });
  const routes = useQuery({
    queryKey: ['map-routes', area],
    queryFn: () => api<RouteCollection>(`/api/v1/map/routes${qs({ area })}`),
  });
  const areas = useQuery({ queryKey: ['areas'], queryFn: () => api<AreaSummary[]>('/api/v1/areas') });

  const visibleAreas = useMemo(
    () => (areas.data ?? []).filter((a) => !area || a.code === area),
    [areas.data, area],
  );
  const canDrawBoundary = ability.can('update', 'Area');
  const canCreate = ability.can('create', 'Asset');
  const canCreateRoute = ability.can('create', 'Route');
  const selectedId = panel?.kind === 'view' ? panel.id : panel?.kind === 'edit' ? panel.asset.id : null;
  const selectedRouteId =
    panel?.kind === 'route'
      ? panel.id
      : panel?.kind === 'route-edit'
        ? panel.route.id
        : mode?.kind === 'shape'
          ? mode.route.id
          : null;

  const assetData = useMemo(() => {
    const hiddenTypes = new Set(LAYER_GROUPS.filter((g) => hidden.has(g.label)).flatMap((g) => g.types));
    return {
      type: 'FeatureCollection' as const,
      features: (features.data?.features ?? []).filter((f) => !hiddenTypes.has(f.properties.type)),
    };
  }, [features.data, hidden]);

  const routeData = useMemo(
    () => ({
      type: 'FeatureCollection' as const,
      features: (routes.data?.features ?? []).filter(
        (f) =>
          !hiddenRoutes.has(f.properties.status) &&
          !hiddenColors.has(f.properties.color ?? '') &&
          !(mode?.kind === 'shape' && f.id === mode.route.id),
      ),
    }),
    [routes.data, hiddenRoutes, hiddenColors, mode],
  );

  const areaData = useMemo(
    () => ({
      type: 'FeatureCollection' as const,
      features: visibleAreas
        .filter((a) => a.boundary)
        .map((a) => ({
          type: 'Feature' as const,
          properties: { code: a.code, name: a.name, color: regionColor(a.code, token) },
          geometry: a.boundary as Geometry,
        })),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- colours resolve from the theme tokens
    [visibleAreas, theme],
  );

  // Route drawing: the second asset click finishes the route (see the click handler).
  const finishRouteRef = useRef<(m: Extract<Mode, { kind: 'route' }>, to: End) => void>(() => {});
  finishRouteRef.current = (m, to) => void finishRoute(m, to);

  // Create the map once.
  useEffect(() => {
    if (!container.current) return;
    const map = new maplibregl.Map({
      container: container.current,
      style: buildStyle('theme'),
      center: INDONESIA_VIEW.center,
      zoom: INDONESIA_VIEW.zoom,
      attributionControl: { compact: true },
      maxPitch: 0,
      dragRotate: false,
    });
    mapRef.current = map;
    map.on('style.load', () => setStyleVersion((v) => v + 1));

    const assetAt = (point: maplibregl.PointLike): End | null => {
      const f = map.getLayer('asset-points')
        ? map.queryRenderedFeatures(point, { layers: ['asset-points'] })[0]
        : undefined;
      if (!f) return null;
      const [lng, lat] = (f.geometry as Point).coordinates as LngLat;
      return {
        id: String(f.properties.id),
        code: String(f.properties.code),
        typeCode: String(f.properties.type),
        lng,
        lat,
      };
    };

    map.on('click', (e) => {
      const m = modeRef.current;
      const { lng, lat } = e.lngLat;
      if (m?.kind === 'add') {
        setMode(null);
        setPanel({ kind: 'create', lat, lng });
        // Keep the new point visible beside the form drawer.
        const width = map.getContainer().clientWidth;
        if (e.point.x > width - 440)
          map.easeTo({ center: [lng, lat], offset: [-Math.min(220, width / 4), 0], duration: 400 });
      } else if (m?.kind === 'move') {
        setMode({ ...m, to: { lat, lng } });
      } else if (m?.kind === 'route') {
        const hit = assetAt(e.point);
        if (hit && !m.from) setMode({ ...m, from: hit });
        else if (hit && m.from && hit.id !== m.from.id) finishRouteRef.current(m, hit);
        else if (!hit && m.from && m.routeMode === 'manual')
          setMode({ ...m, vertices: [...m.vertices, [lng, lat]] });
      } else if (panelRef.current?.kind === 'create') {
        setPanel({ kind: 'create', lat, lng });
      }
    });
    map.on('click', 'clusters', async (e) => {
      if (modeRef.current || panelRef.current?.kind === 'create') return;
      const f = e.features?.[0];
      if (!f) return;
      const src = map.getSource('assets') as GeoJSONSource;
      const zoom = await src.getClusterExpansionZoom(f.properties.cluster_id as number);
      map.easeTo({ center: (f.geometry as Point).coordinates as [number, number], zoom });
    });
    const busy = () =>
      !!modeRef.current ||
      ['create', 'edit', 'route-create', 'route-edit'].includes(panelRef.current?.kind ?? '');
    map.on('click', 'asset-points', (e) => {
      if (busy()) return;
      const f = e.features?.[0];
      if (f) setPanel({ kind: 'view', id: String(f.properties.id) });
    });
    map.on('click', 'route-hit', (e) => {
      if (busy() || assetAt(e.point)) return;
      const f = e.features?.[0];
      if (f) setPanel({ kind: 'route', id: String(f.properties.id) });
    });
    // While drawing a route, a dashed line follows the cursor from the last point.
    map.on('mousemove', (e) => {
      const m = modeRef.current;
      if (m?.kind !== 'route' || !m.from) return;
      const coords: LngLat[] = [[m.from.lng, m.from.lat], ...m.vertices, [e.lngLat.lng, e.lngLat.lat]];
      (map.getSource('draft-route') as GeoJSONSource | undefined)?.setData(line(coords));
    });
    for (const layer of ['clusters', 'asset-points', 'route-hit']) {
      map.on('mouseenter', layer, () => {
        if (!modeRef.current || (modeRef.current.kind === 'route' && layer === 'asset-points'))
          map.getCanvas().style.cursor = 'pointer';
      });
      map.on('mouseleave', layer, () => {
        map.getCanvas().style.cursor = modeRef.current && modeRef.current.kind !== 'shape' ? 'crosshair' : '';
      });
    }
    return () => {
      drawRef.current?.stop();
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // Crosshair while placing points; Esc cancels the mode (shape editing has its own buttons).
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    map.getCanvas().style.cursor =
      (mode && mode.kind !== 'shape') || panel?.kind === 'create' ? 'crosshair' : '';
    if (!mode || mode.kind === 'shape') return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setMode(null);
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [mode, panel?.kind]);

  // Theme or basemap change: rebuild the style (overlays are re-added on style.load).
  const appliedStyle = useRef(`${theme}:theme`);
  useEffect(() => {
    const map = mapRef.current;
    const key = `${theme}:${basemap}`;
    if (!map || drawRef.current || appliedStyle.current === key) return;
    appliedStyle.current = key;
    map.setStyle(buildStyle(basemap), { diff: false });
  }, [theme, basemap]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || styleVersion === 0) return;
    addOverlays(map);
  }, [styleVersion]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || styleVersion === 0) return;
    (map.getSource('assets') as GeoJSONSource | undefined)?.setData(assetData as GjFeatureCollection);
    (map.getSource('areas') as GeoJSONSource | undefined)?.setData(areaData as GjFeatureCollection);
    if (!fittedRef.current && assetData.features.length > 0 && !focus && !routeId) {
      fittedRef.current = true;
      fitTo(
        map,
        assetData.features.map((f) => f.geometry.coordinates),
      );
    }
  }, [assetData, areaData, styleVersion, focus, routeId]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || styleVersion === 0) return;
    (map.getSource('routes') as GeoJSONSource | undefined)?.setData(routeData as GjFeatureCollection);
  }, [routeData, styleVersion]);

  useEffect(() => {
    fittedRef.current = false;
  }, [area]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || styleVersion === 0 || !map.getLayer('asset-mon')) return;
    map.setLayoutProperty('asset-mon', 'visibility', showMon ? 'visible' : 'none');
  }, [showMon, styleVersion]);

  // Selection highlights.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || styleVersion === 0 || !map.getLayer('asset-selected')) return;
    map.setFilter('asset-selected', ['==', ['get', 'id'], selectedId ?? '']);
    map.setFilter('route-selected', ['==', ['get', 'id'], selectedRouteId ?? '']);
  }, [selectedId, selectedRouteId, styleVersion]);

  // Fly to an asset opened from elsewhere (list "Fokus", a port peer, a trace step).
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !flyId || !features.data) return;
    const f = features.data.features.find((x) => x.properties.id === flyId);
    if (f)
      map.flyTo({
        center: f.geometry.coordinates,
        zoom: Math.max(map.getZoom(), 17),
        padding: DRAWER_PADDING,
      });
    setFlyId(null);
  }, [flyId, features.data]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !fitRouteId || !routes.data) return;
    const f = routes.data.features.find((x) => x.properties.id === fitRouteId);
    if (f) fitTo(map, f.geometry.coordinates, true);
    setFitRouteId(null);
  }, [fitRouteId, routes.data]);

  // Draft point (new asset or move target), draft route, trace line and pole preview.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || styleVersion === 0) return;
    const draft = panel?.kind === 'create' ? { lat: panel.lat, lng: panel.lng } : undefined;
    const points: LngLat[] = draft
      ? [[draft.lng, draft.lat]]
      : mode?.kind === 'route'
        ? [...(mode.from ? [[mode.from.lng, mode.from.lat] as LngLat] : []), ...mode.vertices]
        : [];
    (map.getSource('draft') as GeoJSONSource | undefined)?.setData({
      type: 'FeatureCollection',
      features: points.map((c) => ({
        type: 'Feature',
        properties: {},
        geometry: { type: 'Point', coordinates: c },
      })),
    });
    const routeLine: LngLat[] | null =
      panel?.kind === 'route-create'
        ? panel.draft.path
        : mode?.kind === 'route' && mode.from
          ? [[mode.from.lng, mode.from.lat], ...mode.vertices]
          : mode?.kind === 'move' && mode.to
            ? [
                [mode.asset.lng, mode.asset.lat],
                [mode.to.lng, mode.to.lat],
              ]
            : null;
    (map.getSource('draft-route') as GeoJSONSource | undefined)?.setData(
      routeLine && routeLine.length > 1 ? line(routeLine) : EMPTY,
    );
  }, [panel, mode, styleVersion]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || styleVersion === 0) return;
    (map.getSource('trace') as GeoJSONSource | undefined)?.setData(
      traceCoords && traceCoords.length > 1 ? line(traceCoords) : EMPTY,
    );
    if (traceCoords && traceCoords.length > 1) fitTo(map, traceCoords, true);
  }, [traceCoords, styleVersion]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || styleVersion === 0) return;
    (map.getSource('pole-preview') as GeoJSONSource | undefined)?.setData({
      type: 'FeatureCollection',
      features: (polePreview ?? []).map((p) => ({
        type: 'Feature',
        properties: { reason: p.reason },
        geometry: { type: 'Point', coordinates: [p.lng, p.lat] },
      })),
    });
  }, [polePreview, styleVersion]);

  // Moving a point: a draggable marker starts on the asset; a map click or typed coordinates move it too.
  const moveAssetId = mode?.kind === 'move' ? mode.asset.id : null;
  const moveMarker = useRef<maplibregl.Marker | null>(null);
  useEffect(() => {
    const map = mapRef.current;
    const m = modeRef.current;
    if (!map || !moveAssetId || m?.kind !== 'move') return;
    const marker = new maplibregl.Marker({ draggable: true, color: token('primary') })
      .setLngLat([m.asset.lng, m.asset.lat])
      .addTo(map);
    marker.getElement().style.cursor = 'grab';
    marker.on('dragend', () => {
      const { lng, lat } = marker.getLngLat();
      setMode((cur) => (cur?.kind === 'move' ? { ...cur, to: { lat, lng } } : cur));
    });
    moveMarker.current = marker;
    setCoordText(`${m.asset.lat.toFixed(7)}, ${m.asset.lng.toFixed(7)}`);
    setCoordError(null);
    return () => {
      marker.remove();
      moveMarker.current = null;
    };
  }, [moveAssetId]);
  const moveTo = mode?.kind === 'move' ? mode.to : undefined;
  useEffect(() => {
    if (!moveTo) return;
    moveMarker.current?.setLngLat([moveTo.lng, moveTo.lat]);
    setCoordText(`${moveTo.lat.toFixed(7)}, ${moveTo.lng.toFixed(7)}`);
    setCoordError(null);
  }, [moveTo]);

  function applyCoords() {
    if (mode?.kind !== 'move') return;
    const to = parseLatLng(coordText);
    if (!to) {
      setCoordError('Format: lintang, bujur (mis. -6.4268, 106.7315)');
      return;
    }
    setMode({ ...mode, to });
    mapRef.current?.easeTo({ center: [to.lng, to.lat], duration: 500 });
  }

  const onTrace = useCallback((c: LngLat[] | null) => setTraceCoords(c), []);
  const onPolesPreview = useCallback((p: PolePreview['poles'] | null) => setPolePreview(p), []);
  const openAsset = useCallback((id: string) => {
    setPanel({ kind: 'view', id });
    setFlyId(id);
  }, []);

  async function finishRoute(m: Extract<Mode, { kind: 'route' }>, to: End) {
    const from = m.from!;
    setMode(null);
    if (m.routeMode === 'manual') {
      const path: LngLat[] = [[from.lng, from.lat], ...m.vertices, [to.lng, to.lat]];
      setPanel({
        kind: 'route-create',
        draft: { from, to, mode: 'manual', path, lengthM: pathLengthM(path), warning: null },
      });
      return;
    }
    setRouting(true);
    try {
      const p = await send<{ path: LngLat[]; lengthM: number; mode: RouteMode; warning: string | null }>(
        'POST',
        '/api/v1/routes/preview-path',
        {
          fromAssetId: from.id,
          toAssetId: to.id,
          mode: m.routeMode,
        },
      );
      setPanel({
        kind: 'route-create',
        draft: { from, to, mode: p.mode, path: p.path, lengthM: p.lengthM, warning: p.warning },
      });
      if (mapRef.current) fitTo(mapRef.current, p.path, true);
    } catch (err) {
      toast({ tone: 'danger', text: (err as Error).message });
    } finally {
      setRouting(false);
    }
  }

  async function confirmMove() {
    if (mode?.kind !== 'move' || !mode.to) return;
    const { asset, to } = mode;
    setSaving(true);
    try {
      await send('PATCH', `/api/v1/assets/${asset.id}/location`, to);
      await invalidateAssets(queryClient);
      setMode(null);
      toast({
        tone: 'success',
        text: `${asset.code} dipindah ${metres(distanceM([asset.lng, asset.lat], [to.lng, to.lat]))}.`,
        action: {
          label: 'Urungkan',
          onClick: () =>
            void send('PATCH', `/api/v1/assets/${asset.id}/location`, { lat: asset.lat, lng: asset.lng })
              .then(() => invalidateAssets(queryClient))
              .catch((err: Error) => toast({ tone: 'danger', text: err.message })),
        },
      });
    } catch (err) {
      toast({ tone: 'danger', text: (err as Error).message });
    } finally {
      setSaving(false);
    }
  }

  function startDraw(code: string) {
    const map = mapRef.current;
    if (!map) return;
    setPanel(null);
    const draw = new TerraDraw({
      adapter: new TerraDrawMapLibreGLAdapter({ map }),
      modes: [new TerraDrawPolygonMode()],
    });
    draw.start();
    draw.setMode('polygon');
    draw.on('finish', (id) => {
      const feat = draw.getSnapshot().find((x) => x.id === id);
      if (feat) setDrawnGeom(feat.geometry);
    });
    drawRef.current = draw;
    setDrawArea(code);
    setDrawnGeom(null);
  }

  function stopDraw() {
    drawRef.current?.stop();
    drawRef.current = null;
    setDrawArea(null);
    setDrawnGeom(null);
  }

  async function saveBoundary() {
    if (!drawArea || !drawnGeom) return;
    setSaving(true);
    try {
      await send('PUT', `/api/v1/areas/${drawArea}/boundary`, { boundary: drawnGeom });
      toast({ tone: 'success', text: `Batas wilayah ${areaName(drawArea)} tersimpan.` });
      stopDraw();
      await queryClient.invalidateQueries({ queryKey: ['areas'] });
    } catch (err) {
      toast({ tone: 'danger', text: (err as Error).message });
    } finally {
      setSaving(false);
    }
  }

  /** Shape editing (RTE-03): drag vertices, click a midpoint to add one, right-click a vertex to remove it. */
  function startShape(route: RouteDetail) {
    const map = mapRef.current;
    if (!map) return;
    setPanel(null);
    setPolePreview(null);
    const draw = new TerraDraw({
      adapter: new TerraDrawMapLibreGLAdapter({ map }),
      modes: [
        new TerraDrawLineStringMode(),
        new TerraDrawSelectMode({
          flags: {
            linestring: {
              feature: {
                draggable: false,
                coordinates: { midpoints: true, draggable: true, deletable: true },
              },
            },
          },
        }),
      ],
    });
    draw.start();
    const id = crypto.randomUUID();
    draw.addFeatures([
      {
        id,
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: route.path },
        properties: { mode: 'linestring' },
      },
    ]);
    draw.setMode('select');
    draw.selectFeature(id);
    drawRef.current = draw;
    setMode({ kind: 'shape', route });
    fitTo(map, route.path);
  }

  function stopShape(reopen?: string) {
    drawRef.current?.stop();
    drawRef.current = null;
    setMode(null);
    if (reopen) setPanel({ kind: 'route', id: reopen });
  }

  async function saveShape() {
    if (mode?.kind !== 'shape' || !drawRef.current) return;
    const feat = drawRef.current.getSnapshot().find((f) => f.geometry.type === 'LineString');
    if (!feat) return;
    setSaving(true);
    try {
      await send('PUT', `/api/v1/routes/${mode.route.id}/path`, {
        mode: 'manual',
        path: feat.geometry.coordinates,
      });
      await invalidateAssets(queryClient);
      toast({ tone: 'success', text: 'Bentuk jalur tersimpan.' });
      stopShape(mode.route.id);
    } catch (err) {
      toast({ tone: 'danger', text: (err as Error).message });
    } finally {
      setSaving(false);
    }
  }

  const total = features.data?.features.length ?? 0;
  const busyMode = !!drawArea || !!mode;
  const routeCounts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const f of routes.data?.features ?? []) c[f.properties.status] = (c[f.properties.status] ?? 0) + 1;
    return c;
  }, [routes.data]);
  // Line colours in use: a route's own colour (e.g. from Google Earth) or none (status colour).
  const colorGroups = useMemo(() => {
    const groups = new Map<string, { count: number; categories: Set<string> }>();
    for (const f of routes.data?.features ?? []) {
      const key = f.properties.color ?? '';
      const g = groups.get(key) ?? { count: 0, categories: new Set<string>() };
      g.count++;
      g.categories.add(f.properties.category);
      groups.set(key, g);
    }
    return [...groups.entries()].sort((a, b) => b[1].count - a[1].count);
  }, [routes.data]);
  const ownColors = colorGroups.some(([key]) => key !== '');
  const monCounts = useMemo(() => {
    const c = { online: 0, warning: 0, offline: 0, total: 0 };
    for (const f of features.data?.features ?? []) {
      const m = f.properties.mon;
      if (m === 'online' || m === 'warning' || m === 'offline') c[m]++;
      if (m) c.total++;
    }
    return c;
  }, [features.data]);
  const routeKm = useMemo(
    () => (routes.data?.features ?? []).reduce((s, f) => s + f.properties.lengthM, 0),
    [routes.data],
  );

  return (
    <div style={{ position: 'relative', height: '100%', minHeight: 480 }}>
      <div
        ref={container}
        style={{ position: 'absolute', inset: 0 }}
        aria-label="Peta jaringan"
        role="region"
      />

      {/* Layer panel */}
      <section
        className="fn-panel"
        style={{
          position: 'absolute',
          left: 12,
          top: 12,
          width: 250,
          zIndex: 20,
          maxHeight: 'calc(100% - 110px)',
          overflowY: 'auto',
        }}
        aria-label="Layer peta"
      >
        <div className="fn-panel__header">
          <h2 className="fn-panel__title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Layers className="i i--sm" aria-hidden /> Layer Peta
          </h2>
          {(features.isFetching || routes.isFetching) && <span className="fn-spinner" aria-label="Memuat" />}
        </div>
        <div className="fn-panel__body">
          <div className="fn-panel__label">Aset</div>
          {LAYER_GROUPS.map((g) => {
            const count = (features.data?.features ?? []).filter((f) =>
              g.types.includes(f.properties.type),
            ).length;
            return (
              <button
                key={g.label}
                type="button"
                className="fn-layer"
                aria-pressed={!hidden.has(g.label)}
                onClick={() => setHidden((h) => toggle(h, g.label))}
              >
                <span className="fn-layer__glyph">
                  <AssetGlyph type={g.glyph} />
                </span>
                <span className="fn-layer__name">{g.label}</span>
                <span className="fn-layer__count">{nf.format(count)}</span>
              </button>
            );
          })}

          <div className="fn-panel__label">{ownColors ? 'Jalur kabel · pola = status' : 'Jalur kabel'}</div>
          {ROUTE_STATUSES.map((s) => (
            <button
              key={s}
              type="button"
              className="fn-layer"
              aria-pressed={!hiddenRoutes.has(s)}
              onClick={() => setHiddenRoutes((h) => toggle(h, s))}
            >
              <span className="fn-layer__glyph">
                {/* With own line colours in play the status is told by the pattern alone. */}
                <span
                  className={`fn-layer__line fn-legend-line--${s}`}
                  style={ownColors ? { color: 'var(--text-secondary)' } : undefined}
                />
              </span>
              <span className="fn-layer__name">{ROUTE_STATUS_LABELS[s]}</span>
              <span className="fn-layer__count">{nf.format(routeCounts[s] ?? 0)}</span>
            </button>
          ))}

          {ownColors && (
            <>
              <div className="fn-panel__label">Warna garis</div>
              {colorGroups.map(([key, g]) => (
                <button
                  key={key || 'status'}
                  type="button"
                  className="fn-layer"
                  aria-pressed={!hiddenColors.has(key)}
                  onClick={() => setHiddenColors((h) => toggle(h, key))}
                  title={key || 'Warna mengikuti status'}
                >
                  <span className="fn-layer__glyph">
                    <span
                      className="fn-swatch"
                      style={{
                        background:
                          key ||
                          'linear-gradient(90deg, var(--route-installed) 0 34%, var(--route-planned) 34% 67%, var(--route-maintenance) 67%)',
                      }}
                    />
                  </span>
                  <span className="fn-layer__name">
                    <span className="fn-truncate">{key ? colorName(key) : 'Ikut status'}</span>
                    <span className="fn-truncate" style={{ color: 'var(--text-muted)', fontSize: 11 }}>
                      {[...g.categories]
                        .map((c) => ROUTE_CATEGORY_LABELS[c as keyof typeof ROUTE_CATEGORY_LABELS] ?? c)
                        .join(', ')}
                    </span>
                  </span>
                  <span className="fn-layer__count">{nf.format(g.count)}</span>
                </button>
              ))}
            </>
          )}

          {monCounts.total > 0 && (
            <>
              <div className="fn-panel__label">Status perangkat</div>
              <button
                type="button"
                className="fn-layer"
                aria-pressed={showMon}
                onClick={() => setShowMon((v) => !v)}
              >
                <span className="fn-layer__glyph">
                  <span
                    style={{ width: 12, height: 12, borderRadius: 99, border: '2.5px solid var(--success)' }}
                  />
                </span>
                <span className="fn-layer__name">
                  <span style={{ color: 'var(--success-text)' }}>{nf.format(monCounts.online)}</span> ·{' '}
                  <span style={{ color: 'var(--warning-text)' }}>{nf.format(monCounts.warning)}</span> ·{' '}
                  <span style={{ color: 'var(--danger-text)' }}>{nf.format(monCounts.offline)}</span>
                </span>
                <span className="fn-layer__count">on/warn/off</span>
              </button>
            </>
          )}

          <div className="fn-panel__label">Basemap</div>
          <div style={{ padding: '2px 8px 0' }}>
            <div
              className="fn-segmented"
              role="group"
              aria-label="Basemap"
              style={{ width: '100%', boxSizing: 'border-box' }}
            >
              {(
                [
                  ['theme', theme === 'dark' ? 'Gelap' : 'Terang'],
                  ['satellite', 'Satelit'],
                ] as const
              ).map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  className="fn-segmented__item"
                  aria-pressed={basemap === value}
                  disabled={!!drawRef.current}
                  onClick={() => setBasemap(value)}
                  style={{ flex: 1, justifyContent: 'center' }}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="fn-panel__label">Batas wilayah</div>
          {visibleAreas.map((a) => (
            <div key={a.code} className="fn-layer" style={{ cursor: 'default' }}>
              <span className="fn-layer__glyph">
                <span
                  style={{
                    width: 14,
                    height: 10,
                    borderRadius: 2,
                    border: `2px dashed ${regionColor(a.code)}`,
                  }}
                />
              </span>
              <span className="fn-layer__name">{a.name}</span>
              {canDrawBoundary && !busyMode ? (
                <button
                  type="button"
                  className="fn-icon-btn fn-icon-btn--ghost fn-icon-btn--sm"
                  aria-label={`Gambar batas ${a.name}`}
                  title={a.boundary ? 'Gambar ulang batas' : 'Gambar batas'}
                  onClick={() => startDraw(a.code)}
                >
                  <PenLine className="i i--sm" aria-hidden />
                </button>
              ) : (
                <span className="fn-layer__count">{a.boundary ? 'ada' : 'belum'}</span>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Mode bars */}
      {drawArea && (
        <ModeBar drawer={!!panel} icon={<PenLine className="i i--sm" aria-hidden />}>
          <span>
            {drawnGeom
              ? `Batas ${areaName(drawArea)} siap disimpan`
              : `Klik titik-titik batas ${areaName(drawArea)}, klik titik pertama untuk menutup`}
          </span>
          <Button size="sm" variant="ghost" onClick={stopDraw}>
            Batal
          </Button>
          <Button
            size="sm"
            variant="primary"
            disabled={!drawnGeom}
            loading={saving}
            onClick={() => void saveBoundary()}
          >
            Simpan Batas
          </Button>
        </ModeBar>
      )}
      {mode?.kind === 'add' && (
        <ModeBar drawer={!!panel} icon={<MapPin className="i i--sm" aria-hidden />}>
          <span>Klik lokasi aset baru di peta</span>
          <Button size="sm" variant="ghost" onClick={() => setMode(null)}>
            Batal (Esc)
          </Button>
        </ModeBar>
      )}
      {mode?.kind === 'move' && (
        <ModeBar drawer={!!panel} icon={<Move className="i i--sm" aria-hidden />}>
          <span style={{ flexBasis: '100%' }}>
            {mode.to
              ? `Pindahkan ${mode.asset.code} sejauh ${metres(distanceM([mode.asset.lng, mode.asset.lat], [mode.to.lng, mode.to.lat]))}?`
              : `Seret penanda, klik lokasi baru di peta, atau isi koordinat untuk ${mode.asset.code}`}
          </span>
          <form
            style={{ display: 'flex', gap: 6, alignItems: 'center', flex: 1, minWidth: 220 }}
            onSubmit={(e) => {
              e.preventDefault();
              applyCoords();
            }}
          >
            <input
              className="fn-input fn-input--mono"
              style={{ height: 32, flex: 1, minWidth: 0 }}
              aria-label="Koordinat baru (lintang, bujur)"
              aria-invalid={!!coordError}
              title={coordError ?? 'Lintang, bujur — bisa ditempel dari Google Maps'}
              placeholder="-6.4268, 106.7315"
              value={coordText}
              onChange={(e) => {
                setCoordText(e.target.value);
                setCoordError(null);
              }}
            />
            <Button size="sm" type="submit">
              Terapkan
            </Button>
          </form>
          {coordError && (
            <span style={{ flexBasis: '100%', color: 'var(--danger-text)', fontWeight: 500 }}>
              {coordError}
            </span>
          )}
          <Button size="sm" variant="ghost" onClick={() => setMode(null)}>
            Batal
          </Button>
          <Button
            size="sm"
            variant="primary"
            disabled={!mode.to}
            loading={saving}
            onClick={() => void confirmMove()}
          >
            Simpan Posisi
          </Button>
        </ModeBar>
      )}
      {mode?.kind === 'route' && (
        <ModeBar drawer={!!panel} icon={<RouteIcon className="i i--sm" aria-hidden />}>
          <div className="fn-segmented" role="group" aria-label="Tipe jalur">
            {(['road', 'straight', 'manual'] as RouteMode[]).map((rm) => (
              <button
                key={rm}
                type="button"
                className="fn-segmented__item"
                aria-pressed={mode.routeMode === rm}
                onClick={() => setMode({ ...mode, routeMode: rm, vertices: [] })}
              >
                {ROUTE_MODE_LABELS[rm]}
              </button>
            ))}
          </div>
          <span>
            {!mode.from
              ? 'Klik aset asal'
              : mode.routeMode === 'manual'
                ? `Dari ${mode.from.code}: klik titik belokan, lalu klik aset tujuan`
                : `Dari ${mode.from.code}: klik aset tujuan`}
          </span>
          {mode.routeMode === 'manual' && mode.vertices.length > 0 && (
            <Button
              size="sm"
              variant="ghost"
              icon={<Undo2 className="i" aria-hidden />}
              onClick={() => setMode({ ...mode, vertices: mode.vertices.slice(0, -1) })}
            >
              Titik terakhir
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={() => setMode(null)}>
            Batal (Esc)
          </Button>
        </ModeBar>
      )}
      {routing && (
        <ModeBar drawer={!!panel} icon={<RouteIcon className="i i--sm" aria-hidden />}>
          <span className="fn-spinner" aria-hidden /> Menghitung jalur…
        </ModeBar>
      )}
      {mode?.kind === 'shape' && (
        <ModeBar drawer={!!panel} icon={<PenLine className="i i--sm" aria-hidden />}>
          <span>
            Seret titik untuk mengubah bentuk · klik titik tengah untuk menambah · klik kanan titik untuk
            menghapus
          </span>
          <Button size="sm" variant="ghost" onClick={() => stopShape(mode.route.id)}>
            Batal
          </Button>
          <Button size="sm" variant="primary" loading={saving} onClick={() => void saveShape()}>
            Simpan Bentuk
          </Button>
        </ModeBar>
      )}

      {/* Controls */}
      {!busyMode && !['create', 'route-create'].includes(panel?.kind ?? '') && (
        <div style={{ position: 'absolute', top: 12, right: 56, zIndex: 20, display: 'flex', gap: 8 }}>
          {canCreateRoute && (
            <Button
              size="sm"
              icon={<RouteIcon className="i" aria-hidden />}
              style={{ boxShadow: 'var(--shadow-panel)' }}
              onClick={() => {
                setPanel(null);
                setMode({ kind: 'route', routeMode: 'road', vertices: [] });
              }}
            >
              Buat Jalur
            </Button>
          )}
          {canCreate && (
            <Button
              variant="primary"
              size="sm"
              icon={<Plus className="i" aria-hidden />}
              style={{ boxShadow: 'var(--shadow-panel)' }}
              onClick={() => {
                setPanel(null);
                setMode({ kind: 'add' });
              }}
            >
              Tambah Aset
            </Button>
          )}
        </div>
      )}
      <div className="fn-map__controls">
        <div className="fn-map__zoom">
          <button type="button" aria-label="Perbesar" onClick={() => mapRef.current?.zoomIn()}>
            <Plus className="i" aria-hidden />
          </button>
          <button type="button" aria-label="Perkecil" onClick={() => mapRef.current?.zoomOut()}>
            <Minus className="i" aria-hidden />
          </button>
        </div>
        <div className="fn-map__zoom">
          <button
            type="button"
            aria-label="Tampilkan semua aset"
            onClick={() =>
              mapRef.current &&
              fitTo(
                mapRef.current,
                assetData.features.map((f) => f.geometry.coordinates),
              )
            }
          >
            <Maximize className="i" aria-hidden />
          </button>
        </div>
      </div>

      {/* Summary strip */}
      <div
        className="fn-panel"
        style={{ position: 'absolute', left: 12, bottom: 12, zIndex: 20, display: 'flex' }}
      >
        <div style={{ padding: '10px 18px', display: 'flex', flexDirection: 'column', gap: 2 }}>
          <b className="num" style={{ font: '800 18px/22px var(--font-sans)' }}>
            {nf.format(total)}
          </b>
          <span className="fn-hint">aset di peta{area ? ` · ${areaName(area)}` : ''}</span>
        </div>
        <div
          style={{
            padding: '10px 18px',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            borderLeft: '1px solid var(--border-subtle)',
          }}
        >
          <b className="num" style={{ font: '800 18px/22px var(--font-sans)' }}>
            {metres(routeKm)}
          </b>
          <span className="fn-hint">{nf.format(routes.data?.features.length ?? 0)} jalur kabel</span>
        </div>
        {(features.data?.truncated || routes.data?.truncated) && (
          <div
            style={{
              padding: '10px 18px',
              borderLeft: '1px solid var(--border-subtle)',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <span className="fn-hint" style={{ color: 'var(--warning-text)' }}>
              Terlalu banyak data; perbesar peta atau pilih wilayah.
            </span>
          </div>
        )}
      </div>

      {(features.error || routes.error) && (
        <div
          className="fn-alert fn-alert--danger"
          role="alert"
          style={{ position: 'absolute', right: 12, bottom: 12, zIndex: 20, maxWidth: 360 }}
        >
          <div className="fn-alert__body">{(features.error ?? routes.error)!.message}</div>
        </div>
      )}

      {/* Drawers */}
      {panel?.kind === 'view' && (
        <AssetDrawer
          key={panel.id}
          id={panel.id}
          onClose={() => setPanel(null)}
          onEdit={(asset) => setPanel({ kind: 'edit', asset })}
          onOpenAsset={openAsset}
          onMove={(asset) => setMode({ kind: 'move', asset })}
          onStartRoute={(asset) => {
            setPanel(null);
            setMode({
              kind: 'route',
              routeMode: 'road',
              vertices: [],
              from: {
                id: asset.id,
                code: asset.code,
                typeCode: asset.typeCode,
                lng: asset.lng,
                lat: asset.lat,
              },
            });
          }}
          onFocus={(asset) =>
            mapRef.current?.flyTo({
              center: [asset.lng, asset.lat],
              zoom: Math.max(mapRef.current.getZoom(), 17),
              padding: DRAWER_PADDING,
            })
          }
          onTrace={onTrace}
          onDeleted={() => setPanel(null)}
        />
      )}
      {panel?.kind === 'edit' && (
        <AssetForm
          key={`edit-${panel.asset.id}`}
          mode="edit"
          asset={panel.asset}
          onClose={() => setPanel({ kind: 'view', id: panel.asset.id })}
          onSaved={(a) => setPanel({ kind: 'view', id: a.id })}
        />
      )}
      {panel?.kind === 'create' && (
        <AssetForm
          key="create"
          mode="create"
          draft={{ lat: panel.lat, lng: panel.lng }}
          onClose={() => setPanel(null)}
          onSaved={(a) => {
            toast({ tone: 'success', text: `${a.code} ditambahkan.` });
            setPanel({ kind: 'view', id: a.id });
          }}
          pickOnMap
        />
      )}
      {panel?.kind === 'route' && (
        <RouteDrawer
          key={panel.id}
          id={panel.id}
          onClose={() => setPanel(null)}
          onEdit={(route) => setPanel({ kind: 'route-edit', route })}
          onOpenAsset={openAsset}
          onEditShape={startShape}
          onFocus={(route) => mapRef.current && fitTo(mapRef.current, route.path, true)}
          onPolesPreview={onPolesPreview}
          onDeleted={() => setPanel(null)}
        />
      )}
      {panel?.kind === 'route-edit' && (
        <RouteForm
          key={`route-edit-${panel.route.id}`}
          mode="edit"
          route={panel.route}
          onClose={() => setPanel({ kind: 'route', id: panel.route.id })}
          onSaved={(r) => setPanel({ kind: 'route', id: r.id })}
        />
      )}
      {panel?.kind === 'route-create' && (
        <RouteForm
          key="route-create"
          mode="create"
          draft={panel.draft}
          onClose={() => setPanel(null)}
          onSaved={(r) => {
            toast({
              tone: 'success',
              text: `Jalur ${r.from.code} → ${r.to.code} tersimpan (${metres(r.lengthM)}).`,
            });
            setPanel({ kind: 'route', id: r.id });
          }}
        />
      )}
    </div>
  );
}

function ModeBar({ icon, drawer, children }: { icon: ReactNode; drawer: boolean; children: ReactNode }) {
  // Centred over the part of the map that is visible: right of the layer panel and left of an open
  // drawer (fixed to the page's right edge, which is also this container's right edge).
  return (
    <div
      style={{
        position: 'absolute',
        left: 274,
        right: drawer ? 'calc(var(--size-drawer) + 12px)' : 12,
        top: 12,
        zIndex: 30,
        display: 'flex',
        justifyContent: 'center',
        pointerEvents: 'none',
      }}
    >
      <div
        className="fn-panel"
        role="status"
        style={{
          pointerEvents: 'auto',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '8px 8px 8px 14px',
          font: '600 12px/18px var(--font-sans)',
          maxWidth: '100%',
          minWidth: 0,
          boxSizing: 'border-box',
          flexWrap: 'wrap',
        }}
      >
        <span style={{ color: 'var(--primary-text)', display: 'flex' }}>{icon}</span>
        {children}
      </div>
    </div>
  );
}

function toggle(set: Set<string>, key: string): Set<string> {
  const n = new Set(set);
  if (n.has(key)) n.delete(key);
  else n.add(key);
  return n;
}

const line = (coords: LngLat[]): GjFeatureCollection => ({
  type: 'FeatureCollection',
  features: [{ type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: coords } }],
});

function fitTo(map: MlMap, coords: LngLat[], besideDrawer = false) {
  if (coords.length === 0) return;
  const b = new maplibregl.LngLatBounds(coords[0], coords[0]);
  for (const c of coords) b.extend(c);
  // Keep clear of the layer panel (left) and the drawer (right), but never pad more than the canvas allows.
  const { width, height } = map.getContainer().getBoundingClientRect();
  const left = Math.min(290, width * 0.25);
  const right = Math.min(besideDrawer ? 450 : 80, Math.max(40, width - left - 160));
  const vertical = Math.min(80, height * 0.15);
  map.fitBounds(b, { padding: { top: vertical, bottom: vertical, left, right }, maxZoom: 17, duration: 600 });
}

/** Sources and layers for areas, routes, assets, trace, drafts and pole preview, coloured from theme tokens. */
function addOverlays(map: MlMap) {
  const halo = token('map-halo');
  const typeColor = [
    'match',
    ['get', 'type'],
    ...Object.entries(TYPE_COLOR_TOKEN).flatMap(([t, v]) => [t, token(v)]),
    token('asset-odp'),
  ];
  const areaColor = ['coalesce', ['get', 'color'], token('text-muted')];
  const px = (name: string) => parseFloat(token(name)) || 3;
  const routeWidth = [
    'match',
    ['get', 'category'],
    'backbone',
    px('stroke-backbone'),
    'feeder',
    px('stroke-feeder'),
    'distribution',
    px('stroke-distribution'),
    px('stroke-drop'),
  ];

  for (const id of ['areas', 'routes', 'trace', 'draft', 'draft-route', 'pole-preview'])
    if (!map.getSource(id)) map.addSource(id, { type: 'geojson', data: EMPTY });
  if (!map.getSource('assets')) {
    map.addSource('assets', {
      type: 'geojson',
      data: EMPTY,
      cluster: true,
      clusterRadius: 44,
      clusterMaxZoom: 15,
      promoteId: 'id',
      // Clusters hiding an offline device get a red ring.
      clusterProperties: { offline: ['+', ['case', ['==', ['get', 'mon'], 'offline'], 1, 0]] },
    });
  }

  const add = (layer: AddLayerObject) => !map.getLayer(layer.id) && map.addLayer(layer);

  add({
    id: 'area-fill',
    type: 'fill',
    source: 'areas',
    paint: { 'fill-color': areaColor as never, 'fill-opacity': 0.07 },
  });
  add({
    id: 'area-line',
    type: 'line',
    source: 'areas',
    paint: { 'line-color': areaColor as never, 'line-width': 1.5, 'line-dasharray': [4, 3] },
  });
  add({
    id: 'area-label',
    type: 'symbol',
    source: 'areas',
    layout: {
      'text-field': ['upcase', ['get', 'name']],
      'text-font': MARKER_FONT,
      'text-size': 11,
      'text-letter-spacing': 0.08,
    },
    paint: { 'text-color': areaColor as never, 'text-halo-color': token('map-land'), 'text-halo-width': 1.4 },
  });

  // Cable routes (RTE-06): installed solid, planned dashed, maintenance dotted; width by category.
  // A route's own colour (e.g. kept from a Google Earth KMZ) wins over the status colour.
  const routeColor = (status: string) => ['coalesce', ['get', 'color'], token(status)] as never;
  const lineLayout = { 'line-cap': 'round', 'line-join': 'round' } as const;
  add({
    id: 'route-selected',
    type: 'line',
    source: 'routes',
    filter: ['==', ['get', 'id'], ''],
    layout: lineLayout,
    paint: { 'line-color': token('route-trace-glow'), 'line-width': 14 },
  });
  add({
    id: 'route-halo',
    type: 'line',
    source: 'routes',
    layout: lineLayout,
    paint: { 'line-color': halo, 'line-width': ['+', routeWidth, 2.5] as never, 'line-opacity': 0.7 },
  });
  add({
    id: 'route-installed',
    type: 'line',
    source: 'routes',
    filter: ['==', ['get', 'status'], 'installed'],
    layout: lineLayout,
    paint: { 'line-color': routeColor('route-installed'), 'line-width': routeWidth as never },
  });
  add({
    id: 'route-planned',
    type: 'line',
    source: 'routes',
    filter: ['==', ['get', 'status'], 'planned'],
    paint: {
      'line-color': routeColor('route-planned'),
      'line-width': routeWidth as never,
      'line-dasharray': [2.5, 2],
    },
  });
  add({
    id: 'route-maintenance',
    type: 'line',
    source: 'routes',
    filter: ['==', ['get', 'status'], 'maintenance'],
    paint: {
      'line-color': routeColor('route-maintenance'),
      'line-width': routeWidth as never,
      'line-dasharray': [0.6, 2],
    },
  });
  add({
    id: 'route-hit',
    type: 'line',
    source: 'routes',
    layout: lineLayout,
    paint: { 'line-color': '#000', 'line-opacity': 0, 'line-width': 12 },
  });

  add({
    id: 'trace-glow',
    type: 'line',
    source: 'trace',
    layout: lineLayout,
    paint: { 'line-color': token('route-trace-glow'), 'line-width': 12 },
  });
  add({
    id: 'trace-line',
    type: 'line',
    source: 'trace',
    layout: lineLayout,
    paint: { 'line-color': token('route-trace'), 'line-width': 3.5 },
  });
  add({
    id: 'draft-route-line',
    type: 'line',
    source: 'draft-route',
    paint: { 'line-color': token('primary'), 'line-width': 3, 'line-dasharray': [2, 1.5] },
  });

  add({
    id: 'clusters',
    type: 'circle',
    source: 'assets',
    filter: ['has', 'point_count'],
    paint: {
      'circle-color': token('bg-surface-elevated'),
      'circle-radius': ['step', ['get', 'point_count'], 16, 50, 20, 200, 24],
      // A muted ring keeps clusters readable on both dark and light basemaps.
      'circle-stroke-color': [
        'case',
        ['>', ['get', 'offline'], 0],
        token('danger'),
        token('text-muted'),
      ] as never,
      'circle-stroke-width': ['case', ['>', ['get', 'offline'], 0], 3, 2] as never,
      'circle-stroke-opacity': ['case', ['>', ['get', 'offline'], 0], 1, 0.7] as never,
    },
  });
  add({
    id: 'cluster-count',
    type: 'symbol',
    source: 'assets',
    filter: ['has', 'point_count'],
    layout: { 'text-field': ['get', 'point_count_abbreviated'], 'text-font': MARKER_FONT, 'text-size': 11.5 },
    paint: { 'text-color': token('text-primary') },
  });

  const radius = [
    'match',
    ['get', 'type'],
    ['OLT', 'SERVER', 'AP'],
    10,
    ['ODC', 'ODP'],
    8,
    'JC',
    7,
    'POLE',
    4,
    'CPE',
    4.5,
    6,
  ];
  add({
    id: 'asset-selected',
    type: 'circle',
    source: 'assets',
    filter: ['==', ['get', 'id'], ''],
    paint: {
      'circle-radius': ['+', radius, 6] as never,
      'circle-color': 'rgba(0,0,0,0)',
      'circle-stroke-color': token('primary'),
      'circle-stroke-width': 3,
    },
  });
  // Monitoring ring (MON-03): green online, amber warning, red offline, grey maintenance.
  add({
    id: 'asset-mon',
    type: 'circle',
    source: 'assets',
    filter: [
      'all',
      ['!', ['has', 'point_count']],
      ['in', ['get', 'mon'], ['literal', ['online', 'warning', 'offline', 'maintenance']]],
    ],
    paint: {
      'circle-radius': ['+', radius, 4.5] as never,
      'circle-color': 'rgba(0,0,0,0)',
      'circle-stroke-width': ['case', ['==', ['get', 'mon'], 'offline'], 3, 2.2] as never,
      'circle-stroke-color': [
        'match',
        ['get', 'mon'],
        'online',
        token('success'),
        'warning',
        token('warning'),
        'offline',
        token('danger'),
        token('text-muted'),
      ] as never,
    },
  });
  add({
    id: 'asset-points',
    type: 'circle',
    source: 'assets',
    filter: ['!', ['has', 'point_count']],
    paint: {
      'circle-radius': radius as never,
      'circle-color': typeColor as never,
      'circle-stroke-color': halo,
      'circle-stroke-width': 2,
      'circle-opacity': ['case', ['==', ['get', 'status'], 'planned'], 0.55, 1],
      'circle-stroke-opacity': ['case', ['==', ['get', 'status'], 'planned'], 0.55, 1],
    },
  });
  add({
    id: 'asset-labels',
    type: 'symbol',
    source: 'assets',
    filter: [
      'all',
      ['!', ['has', 'point_count']],
      ['!', ['in', ['get', 'type'], ['literal', ['POLE', 'CPE']]]],
    ],
    minzoom: 15,
    layout: {
      'text-field': ['get', 'code'],
      'text-font': LABEL_FONT,
      'text-size': 10.5,
      'text-offset': [0, 1.4],
      'text-anchor': 'top',
    },
    paint: { 'text-color': token('text-primary'), 'text-halo-color': halo, 'text-halo-width': 1.4 },
  });

  add({
    id: 'pole-preview-points',
    type: 'circle',
    source: 'pole-preview',
    paint: {
      'circle-radius': 5,
      'circle-color': [
        'case',
        ['==', ['get', 'reason'], 'turn'],
        token('warning'),
        token('asset-pole'),
      ] as never,
      'circle-stroke-color': token('primary'),
      'circle-stroke-width': 2,
    },
  });
  add({
    id: 'draft-point',
    type: 'circle',
    source: 'draft',
    paint: {
      'circle-radius': 7,
      'circle-color': token('primary'),
      'circle-opacity': 0.35,
      'circle-stroke-color': token('primary'),
      'circle-stroke-width': 3,
    },
  });
}
